import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chartjs } from 'app/trello/chartjs.imports';
import { GroupedMap, GroupedMap2D } from 'app/trello/models/board-summary';
import { ChartableData, LabelledValues } from 'app/trello/models/charts';
import { Templating } from 'app/trello/templating.imports';
import { StatisticsExtractor } from 'app/trello/utility/statistics-extractor';
import { array } from 'app/trello/utility/utility-functions';
import { Chart, ChartOptions, ChartType, Color } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Chartjs,
    Templating,
    ContextMenuModule,
    DialogModule,
  ],
  selector: 'app-simple-chart-with-table',
  styleUrls: ['./simple-chart-with-table.component.css'],
  templateUrl: './simple-chart-with-table.component.html',
})
export class SimpleChartWithTableComponent<T, U> implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input({required: true}) chartData!:
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>> |
    ChartableData<LabelledValues, GroupedMap<T[]> | GroupedMap2D<U[]>>[];

  @Input() chartType: ChartType = 'bar';
  @Input() chartLegend: boolean = true;
  @Input() chartOptions: ChartOptions = {};

  @Input() chartContainer: HTMLElement | undefined = undefined;

  @Input() tableTitle: string | undefined;
  @Input() tableGroupColumnLabel: string = 'Group';

  @ViewChild('canvasContainer') protected canvasContainer!: ElementRef;
  @ViewChild(BaseChartDirective) protected chartDirective!: BaseChartDirective;

  private chartContainerResizeObserver: ResizeObserver | undefined;

  private get chartInstance(): Chart | undefined {
    return this.chartDirective?.chart;
  }

  private initialized: boolean = false;
  private initializedView: boolean = false;

  protected readonly canvasContextMenuItems = [
    {
      label: 'View Data',
      command: () => {
        this.showDialog();
      }
    }
  ];

  protected mappedChartAxisLabels: string[] = [];

  protected mappedChartDatasets: {
    data: number[];
    label: string | undefined;
    backgroundColor?: Color | Color[];
    borderColor?: Color | Color[];
    fill?: boolean;
    barPercentage?: number;
    categoryPercentage?: number;
  }[] = [];

  protected mappedChartDatasetsReady: boolean = false;

  protected mappedTableValues: {
    group: string;
    sum: number;
    individual?: {
      [tableValueIndex: string]: {
        group: string;
        sum: number;
      }
    },
  }[] = [];

  protected mappedTableValuesReady: boolean = false;

  protected multipleDataPoints: boolean = false;
  protected tableDataAvailable: boolean = false;

  protected tableDialogOpen: boolean = false;

  protected readonly array = array;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly statisticsExtractor: StatisticsExtractor,
  ) {
  }

  public ngOnInit(): void {

    this.initialized = true;

    this.prepareData();
  }

  public ngAfterViewInit(): void {

    this.initializedView = true;

    this.keepCanvasSizeSameAsChartContainer();
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartContainer']) {
      this.keepCanvasSizeSameAsChartContainer();
    }

    if (changes['chartData']) {

      this.prepareData();

      if (this.chartInstance) {
        this.chartDirective.update();
      }
    }
  }

  public ngOnDestroy(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }

  protected showDialog(): void {

    this.tableDialogOpen = true;

    this.changeDetector.detectChanges();
  }

  private keepCanvasSizeSameAsChartContainer(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (!this.chartContainer
      || !this.initializedView) {
      return;
    }

    this.chartContainerResizeObserver =
      new ResizeObserver((entries) => {

        for (const entry of entries) {

          if (entry.target === this.chartContainer) {

            let {width, height} = entry.contentRect;

            width = Math.round(width);
            height = Math.round(height);

            if (this.canvasContainer) {

              this.canvasContainer.nativeElement.style.width = `${width}px`;
              this.canvasContainer.nativeElement.style.height = `${height}px`;
            }
          }
        }
      });

    this.chartContainerResizeObserver.observe(this.chartContainer);
  }

  private prepareData(): void {

    this.prepareChartDatasets();
    this.prepareTableValues();

    this.multipleDataPoints =
      Array.isArray(this.chartData)
      && this.chartData.length > 1;
  }

  private prepareChartDatasets(): void {

    if (!this.initialized) {
      return;
    }

    const chartDataPoints =
      Array.isArray(this.chartData)
        ? this.chartData
        : [this.chartData as ChartableData<LabelledValues, unknown>];

    if (chartDataPoints.length === 0) {

      this.mappedChartAxisLabels = [];

      this.mappedChartDatasets = [];
      this.mappedChartDatasetsReady = true;

      return;
    }

    const firstDatasetLabels = chartDataPoints[0].chartData.labels || [];

    const allLabelsSame =
      chartDataPoints
        .map((chartDataPoint) => chartDataPoint.chartData)
        .map((labelledValues) => labelledValues.labels || [])
        .every((labels: string[]) => {

          if (labels.length !== firstDatasetLabels.length) {
            return false;
          }

          return labels.every((label, index) => label === firstDatasetLabels[index]);
        });

    if (allLabelsSame) {

      this.mappedChartAxisLabels = [...firstDatasetLabels];

    } else {

      const uniqueLabels = new Set<string>();

      chartDataPoints
        .map((chartDataPoint) => chartDataPoint.chartData)
        .map((labelledValues) => labelledValues.labels || [])
        .forEach((labels: string[]) => {

          [...labels].forEach((label: string) => uniqueLabels.add(label));
        });

      this.mappedChartAxisLabels = Array.from(uniqueLabels).sort();
    }

    this.mappedChartDatasets =
      chartDataPoints
        .map((chartDataPoint: ChartableData<LabelledValues, unknown>) => {

          const {labels, values} = chartDataPoint.chartData;

          let alignedData: number[];

          if (allLabelsSame) {

            alignedData =
              values && values.length === this.mappedChartAxisLabels.length
                ? [...values]
                : this.mappedChartAxisLabels.map((_: string, index: number) => values ? values[index] || 0 : 0);

          } else {

            const valueByLabel = new Map<string, number>();

            if (labels && values && labels.length === values.length) {

              labels.forEach((label: string, index: number) => {
                valueByLabel.set(label, values![index]);
              });
            }

            alignedData = this.mappedChartAxisLabels.map((label: string): number => valueByLabel.get(label) || 0);
          }

          return this.toChartDataset(
            alignedData,
            chartDataPoint.chartLabel,
            chartDataPoint.primaryColor,
            chartDataPoint.secondaryColor,
            chartDataPoint.fill
          );
        });

    this.mappedChartDatasetsReady = true;
  }

  private prepareTableValues(): void {

    if (!this.initialized) {
      return;
    }

    if (!Array.isArray(this.chartData)) {

      this.mappedTableValues =
        this.statisticsExtractor.aggregateCountsByGroup(this.chartData.tableData)
          .map((groupCount) => ({group: groupCount.group, sum: groupCount.count}));

      this.mappedTableValuesReady = true;

      this.tableDataAvailable = this.mappedTableValues.length > 0;

      return;
    }

    const groupCountsPerDataset =
      this.chartData.map((chartDataPoint) => this.statisticsExtractor.aggregateCountsByGroup(chartDataPoint.tableData));

    const groupCountsPerDatasetLookup: Map<string, number>[] =
      groupCountsPerDataset.map(
        (groupCounts) => new Map(
          groupCounts.map((groupCount) => [groupCount.group, groupCount.count]),
        ),
      );

    const allGroups =
      new Set<string>(
        groupCountsPerDataset.flatMap((groupCounts) =>
          groupCounts.map((groupCount) => groupCount.group),
        )
      );

    this.mappedTableValues =
      Array.from(allGroups).sort()
        .map((group) => {

          const individual: {
            [tableValueIndex: string]: {
              group: string;
              sum: number;
            }
          } = {};

          let aggregatedSum = 0;

          groupCountsPerDatasetLookup.forEach((groupCounts, i) => {

            const count = groupCounts.get(group) || 0;

            aggregatedSum += count;

            individual[i] = {group, sum: count};
          });

          return {
            group,
            sum: aggregatedSum,
            individual,
          };
        });

    this.mappedTableValuesReady = true;

    this.tableDataAvailable = this.mappedTableValues.length > 0;
  }

  private toChartDataset(
    data: number[],
    label: string | undefined,
    backgroundColor: Color | Color[] | undefined,
    borderColor: Color | Color[] | undefined,
    fill: boolean = false,
  ) {

    return {
      data,
      label,
      backgroundColor,
      borderColor,
      fill,
    };
  }

}
